import { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate, Link, useSearchParams } from "react-router-dom";
import { useEditorAuth } from "@/context/EditorAuthContext";
import { ArrowLeft, Save, Trash2, Plus, X, Sparkles, Image as ImageIcon, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API, resolveMediaUrl } from "@/lib/api";

const empty = {
  title: "", slug: "", excerpt: "", hero_image: "", body: "",
  tags: [], meta_description: "", faq: [], author_name: "Happy Home Agency",
  status: "draft", publish_at: "",
};

export default function ArticleEditor() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === "new";
  const { user, loading, authAxios } = useEditorAuth();
  const [form, setForm] = useState(empty);
  const [tagInput, setTagInput] = useState("");
  const [loadingArticle, setLoadingArticle] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [aiModal, setAiModal] = useState(false);
  const [aiKeyword, setAiKeyword] = useState("");
  const [aiNotes, setAiNotes] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [imgLoading, setImgLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const navigate = useNavigate();

  // If editor arrives via /editor/articles/new?keyword=... auto-open AI modal with keyword prefilled
  useEffect(() => {
    if (isNew) {
      const kw = searchParams.get("keyword");
      if (kw) {
        setAiKeyword(kw);
        setAiModal(true);
      }
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!user || isNew) return;
    authAxios().get(`/editor/articles/${id}`).then((r) => {
      const d = r.data;
      setForm({
        title: d.title || "", slug: d.slug || "", excerpt: d.excerpt || "",
        hero_image: d.hero_image || "", body: d.body || "",
        tags: d.tags || [], meta_description: d.meta_description || "",
        faq: d.faq || [], author_name: d.author_name || "Happy Home Agency",
        status: d.status || "draft", publish_at: d.publish_at || "",
      });
    }).catch(() => toast.error("Failed to load article")).finally(() => setLoadingArticle(false));
    // eslint-disable-next-line
  }, [id, user]);

  if (loading) return null;
  if (!user) return <Navigate to="/editor/login" replace />;
  if (loadingArticle) return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>;

  const update = (k, v) => setForm({ ...form, [k]: v });

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !form.tags.includes(t)) setForm({ ...form, tags: [...form.tags, t] });
    setTagInput("");
  };
  const removeTag = (t) => setForm({ ...form, tags: form.tags.filter(x => x !== t) });

  const addFAQ = () => setForm({ ...form, faq: [...form.faq, { question: "", answer: "" }] });
  const updateFAQ = (i, k, v) => setForm({ ...form, faq: form.faq.map((f, idx) => idx === i ? { ...f, [k]: v } : f) });
  const removeFAQ = (i) => setForm({ ...form, faq: form.faq.filter((_, idx) => idx !== i) });

  const runAI = async () => {
    if (!aiKeyword.trim()) { toast.error("Enter a keyword or topic"); return; }
    setAiLoading(true);
    try {
      const r = await authAxios().post("/editor/ai/article", { keyword: aiKeyword.trim(), notes: aiNotes.trim() }, { timeout: 180000 });
      const d = r.data;
      setForm(prev => ({
        ...prev,
        title: d.title || prev.title,
        slug: d.slug || prev.slug,
        excerpt: d.excerpt || prev.excerpt,
        meta_description: d.meta_description || prev.meta_description,
        body: d.body || prev.body,
        tags: d.tags && d.tags.length ? d.tags : prev.tags,
        faq: d.faq && d.faq.length ? d.faq : prev.faq,
        author_name: d.author_name || prev.author_name,
      }));
      toast.success("Article drafted — review, tweak, then publish.");
      setAiModal(false);
      setAiKeyword("");
      setAiNotes("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed. Try again.");
    } finally {
      setAiLoading(false);
    }
  };

  const runImage = async () => {
    const prompt = form.title.trim() || form.excerpt.trim();
    if (!prompt) { toast.error("Add a title first — the image is generated from it."); return; }
    setImgLoading(true);
    try {
      const r = await authAxios().post("/editor/ai/image", { prompt }, { timeout: 120000 });
      const url = r.data?.url;
      if (url) {
        // Store the relative path — resolveMediaUrl() at render time will prepend REACT_APP_BACKEND_URL
        // so the same DB value works on both preview and the live domain.
        setForm(prev => ({ ...prev, hero_image: url }));
        toast.success("Featured image generated");
      } else {
        toast.error("No image returned");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Image generation failed. Try again.");
    } finally {
      setImgLoading(false);
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    // Reset input so re-selecting the same file also fires onChange
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Image is larger than 8 MB"); return; }
    setUploadLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await authAxios().post("/editor/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const url = r.data?.url;
      if (url) {
        setForm(prev => ({ ...prev, hero_image: url }));
        toast.success("Image uploaded to Object Store");
      } else {
        toast.error("No URL returned");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed. Try again.");
    } finally {
      setUploadLoading(false);
    }
  };

  const save = async (nextStatus) => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    const payload = { ...form, status: nextStatus || form.status };
    if (!payload.publish_at) delete payload.publish_at;
    setSaving(true);
    try {
      if (isNew) {
        const r = await authAxios().post("/editor/articles", payload);
        toast.success(nextStatus === "published" ? "Published!" : "Saved");
        navigate(`/editor/articles/${r.data.id}`, { replace: true });
      } else {
        await authAxios().put(`/editor/articles/${id}`, payload);
        toast.success(nextStatus === "published" ? "Published!" : "Saved");
        // refresh slug if changed
        const r = await authAxios().get(`/editor/articles/${id}`);
        setForm(prev => ({ ...prev, slug: r.data.slug, status: r.data.status }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="article-editor-page">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-4 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Link to="/editor" className="text-sm text-gray-500 hover:text-[#0A1128] flex items-center gap-2"><ArrowLeft className="w-4 h-4"/>Back</Link>
            <div className="text-sm text-gray-400">|</div>
            <div className="text-sm">
              <span className="text-[#0A1128] font-medium">{isNew ? "New Article" : "Editing"}</span>
              <span className={`ml-3 text-xs px-2 py-1 border ${
                form.status === "published" ? "border-[#688A4E] text-[#688A4E]" :
                form.status === "scheduled" ? "border-amber-500 text-amber-600" :
                "border-gray-300 text-gray-500"
              }`}>{form.status}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setAiModal(true)} type="button" data-testid="ai-generate-open" className="text-sm flex items-center gap-2 border border-[#688A4E] text-[#688A4E] px-4 py-2 hover:bg-[#688A4E] hover:text-white transition">
              <Sparkles className="w-4 h-4"/> Generate with AI
            </button>
            <button onClick={() => save("draft")} disabled={saving} data-testid="editor-save-draft" className="btn-outline text-sm disabled:opacity-60">
              <Save className="w-4 h-4"/> Save Draft
            </button>
            <button onClick={() => save("published")} disabled={saving} data-testid="editor-publish" className="btn-primary text-sm disabled:opacity-60">
              {form.status === "published" ? "Update" : "Publish"}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-8">
          <div>
            <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">Title</label>
            <input data-testid="article-title" type="text" value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="Article title…" className="w-full font-serif text-4xl text-[#0A1128] border-b border-gray-300 py-3 bg-transparent focus:outline-none focus:border-[#688A4E]"/>
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">Slug (URL)</label>
            <div className="flex items-center border-b border-gray-300 focus-within:border-[#688A4E]">
              <span className="text-gray-400 text-sm py-3">/blog/</span>
              <input data-testid="article-slug" type="text" value={form.slug} onChange={(e) => update("slug", e.target.value)} placeholder="auto-generated from title" className="flex-1 py-3 bg-transparent focus:outline-none text-sm"/>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs tracking-widest uppercase text-gray-500">Hero Image</label>
              <div className="flex items-center gap-4">
                <label className={`text-xs flex items-center gap-1.5 cursor-pointer text-[#0A1128] hover:text-[#688A4E] ${uploadLoading ? "opacity-50 pointer-events-none" : ""}`} data-testid="upload-image-label">
                  {uploadLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Upload className="w-3.5 h-3.5"/>}
                  {uploadLoading ? "Uploading…" : "Upload image"}
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={uploadImage} className="hidden" data-testid="upload-image-input" disabled={uploadLoading}/>
                </label>
                <button onClick={runImage} disabled={imgLoading} type="button" data-testid="ai-generate-image" className="text-xs flex items-center gap-1.5 text-[#688A4E] hover:text-[#56763E] disabled:opacity-50">
                  {imgLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <ImageIcon className="w-3.5 h-3.5"/>}
                  {imgLoading ? "Generating…" : "Generate with AI"}
                </button>
              </div>
            </div>
            <input data-testid="article-hero" type="url" value={form.hero_image} onChange={(e) => update("hero_image", e.target.value)} placeholder="/api/objstore/… — click Upload image or Generate with AI" className="w-full border-b border-gray-300 py-3 bg-transparent focus:outline-none focus:border-[#688A4E]"/>
            {form.hero_image && <img src={resolveMediaUrl(form.hero_image)} alt="Hero preview" className="mt-4 w-full h-64 object-cover"/>}
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">Excerpt (short summary)</label>
            <textarea data-testid="article-excerpt" value={form.excerpt} onChange={(e) => update("excerpt", e.target.value)} rows={3} className="w-full border border-gray-300 p-4 bg-transparent focus:outline-none focus:border-[#688A4E]"/>
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">Body (Markdown supported)</label>
            <textarea data-testid="article-body" value={form.body} onChange={(e) => update("body", e.target.value)} rows={20} placeholder={`# Section heading\n\nWrite your article here. **Bold**, *italic*, [links](https://example.com), lists, etc.\n\n## Another heading\n\nMore content…`} className="w-full border border-gray-300 p-4 bg-transparent focus:outline-none focus:border-[#688A4E] font-mono text-sm"/>
          </div>

          {/* FAQ */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-xs tracking-widest uppercase text-gray-500">FAQ</label>
              <button onClick={addFAQ} data-testid="add-faq" type="button" className="text-xs text-[#688A4E] flex items-center gap-1"><Plus className="w-3 h-3"/>Add question</button>
            </div>
            <div className="space-y-4">
              {form.faq.map((f, i) => (
                <div key={i} className="border border-gray-200 p-4 bg-white">
                  <div className="flex items-start justify-between mb-2">
                    <div className="text-xs text-gray-400">Q{i+1}</div>
                    <button onClick={() => removeFAQ(i)} type="button" className="text-gray-400 hover:text-red-600"><X className="w-4 h-4"/></button>
                  </div>
                  <input data-testid={`faq-q-${i}`} value={f.question} onChange={(e) => updateFAQ(i, "question", e.target.value)} placeholder="Question" className="w-full border-b border-gray-200 py-2 bg-transparent focus:outline-none focus:border-[#688A4E] text-sm mb-2"/>
                  <textarea data-testid={`faq-a-${i}`} value={f.answer} onChange={(e) => updateFAQ(i, "answer", e.target.value)} rows={3} placeholder="Answer" className="w-full border border-gray-200 p-2 bg-transparent focus:outline-none focus:border-[#688A4E] text-sm"/>
                </div>
              ))}
              {form.faq.length === 0 && <div className="text-xs text-gray-400 border border-dashed border-gray-300 p-4 text-center">No FAQs yet</div>}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-1 space-y-6">
          <div className="border border-gray-200 p-6 bg-white">
            <div className="overline mb-4">Meta</div>
            <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">Meta description</label>
            <textarea data-testid="article-meta" value={form.meta_description} onChange={(e) => update("meta_description", e.target.value)} rows={3} maxLength={160} className="w-full border border-gray-300 p-3 bg-transparent focus:outline-none focus:border-[#688A4E] text-sm mb-1"/>
            <div className="text-xs text-gray-400 mb-4">{form.meta_description.length}/160 chars</div>
            <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">Author</label>
            <input data-testid="article-author" type="text" value={form.author_name} onChange={(e) => update("author_name", e.target.value)} className="w-full border-b border-gray-300 py-2 bg-transparent focus:outline-none focus:border-[#688A4E] text-sm"/>
          </div>

          <div className="border border-gray-200 p-6 bg-white">
            <div className="overline mb-4">Tags</div>
            <div className="flex gap-2 mb-3">
              <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} data-testid="tag-input" placeholder="Add tag & Enter" className="flex-1 border-b border-gray-300 py-2 bg-transparent focus:outline-none focus:border-[#688A4E] text-sm"/>
              <button onClick={addTag} type="button" className="text-xs text-[#688A4E]">Add</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.tags.map((t) => (
                <span key={t} className="text-xs border border-gray-300 px-2 py-1 flex items-center gap-1.5">
                  {t}
                  <button onClick={() => removeTag(t)} type="button" className="text-gray-400 hover:text-red-600"><X className="w-3 h-3"/></button>
                </span>
              ))}
              {form.tags.length === 0 && <div className="text-xs text-gray-400">No tags</div>}
            </div>
          </div>

          <div className="border border-gray-200 p-6 bg-white">
            <div className="overline mb-4">Schedule</div>
            <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">Publish at (optional)</label>
            <input data-testid="article-schedule" type="datetime-local" value={form.publish_at ? form.publish_at.slice(0, 16) : ""} onChange={(e) => update("publish_at", e.target.value ? new Date(e.target.value).toISOString() : "")} className="w-full border-b border-gray-300 py-2 bg-transparent focus:outline-none focus:border-[#688A4E] text-sm"/>
            <div className="mt-4 flex gap-2">
              <button onClick={() => save("scheduled")} disabled={saving || !form.publish_at} data-testid="editor-schedule" className="btn-outline text-xs w-full justify-center disabled:opacity-40">Schedule</button>
            </div>
          </div>

          {!isNew && (
            <div className="border border-red-200 p-6 bg-white">
              <div className="overline text-red-600 mb-2">Danger Zone</div>
              <p className="text-xs text-gray-500 mb-4">Deleting is permanent.</p>
              <button
                onClick={async () => {
                  if (!window.confirm("Delete this article?")) return;
                  await authAxios().delete(`/editor/articles/${id}`);
                  toast.success("Deleted");
                  navigate("/editor");
                }}
                data-testid="editor-delete-article"
                className="text-red-600 border border-red-200 hover:bg-red-50 px-4 py-2 text-sm flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4"/> Delete article
              </button>
            </div>
          )}
        </aside>
      </div>

      {/* AI Generation Modal */}
      {aiModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6" onClick={() => !aiLoading && setAiModal(false)} data-testid="ai-modal">
          <div className="bg-white max-w-xl w-full p-8 sm:p-10" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 border border-[#688A4E] flex items-center justify-center"><Sparkles className="w-5 h-5 text-[#688A4E]"/></div>
                <div>
                  <div className="overline">Happy Home AI</div>
                  <div className="font-serif text-2xl text-[#0A1128]">Generate an article</div>
                </div>
              </div>
              {!aiLoading && (
                <button onClick={() => setAiModal(false)} className="text-gray-400 hover:text-[#0A1128]"><X className="w-5 h-5"/></button>
              )}
            </div>

            <p className="text-sm text-gray-600 mb-6">Type a keyword or topic. Claude Sonnet drafts a full ~2,000-word SEO article with title, tags, FAQ and CTA — tuned to Happy Home Agency's brand voice.</p>

            <div className="space-y-5">
              <div>
                <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">Primary keyword / topic *</label>
                <input
                  data-testid="ai-keyword"
                  value={aiKeyword}
                  onChange={(e) => setAiKeyword(e.target.value)}
                  placeholder="e.g. Filipino helper salary Singapore 2026"
                  className="w-full border-b border-gray-300 py-3 bg-transparent focus:outline-none focus:border-[#688A4E]"
                  disabled={aiLoading}
                />
              </div>
              <div>
                <label className="block text-xs tracking-widest uppercase text-gray-500 mb-2">Notes / angle (optional)</label>
                <textarea
                  data-testid="ai-notes"
                  value={aiNotes}
                  onChange={(e) => setAiNotes(e.target.value)}
                  rows={3}
                  placeholder="e.g. Focus on first-time employers, include MOM levy rates, mention 365-day care."
                  className="w-full border border-gray-300 p-3 bg-transparent focus:outline-none focus:border-[#688A4E] text-sm"
                  disabled={aiLoading}
                />
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between gap-4">
              <div className="text-xs text-gray-400">Takes about 60–90 seconds.</div>
              <div className="flex gap-3">
                {!aiLoading && (
                  <button onClick={() => setAiModal(false)} type="button" className="text-sm text-gray-500 hover:text-[#0A1128] px-4 py-2">Cancel</button>
                )}
                <button
                  onClick={runAI}
                  disabled={aiLoading || !aiKeyword.trim()}
                  data-testid="ai-generate-submit"
                  className="btn-primary text-sm disabled:opacity-60"
                >
                  {aiLoading ? <><Loader2 className="w-4 h-4 animate-spin"/> Drafting…</> : <><Sparkles className="w-4 h-4"/> Generate</>}
                </button>
              </div>
            </div>

            {aiLoading && (
              <div className="mt-6 text-xs text-gray-500 border-l-2 border-[#688A4E] pl-3 italic">
                Claude is researching, writing and structuring your article. Please keep this window open.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
